import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useAtom, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';

import { Company } from '@restworld/utility-types';
import { SkeletonEmployerCardLoading } from '@restworld/ui-ds';
import { IndexStripePaymentMethods } from '@restworld/data-services';

import useEmployerCompanyListQuery from './hooks/use-employer-company-list-query';
import useEmployerStripePaymentMethodsQuery from './hooks/use-employer-stripe-payment-methods';
import {
  currentCompanyActionAtom,
  isEmployerCompanyActionModalOpenAtom,
  lastCompanySearchValueAtom,
  newlyCreatedCompanyIdAtom,
  selectedCompanyAtom,
} from './employer-companies.atoms';
import { CardsWrapper } from './employer-companies.styled';
import { CompanyPaymentInfoCard, EmptyCompanyPaymentCard } from '../shared/company-payment-info-card';

function EmployerCompanyList({ context, limit = 1 }: { context: string; limit?: number }) {
  const router = useRouter();
  const { companyList, companyListItemsEl, isLoading, loadingItemsEl } = useEmployerCompanyList({ context, limit });

  const setCurrentCompanyAction = useSetAtom(currentCompanyActionAtom);
  const setLastCompanySearchValue = useSetAtom(lastCompanySearchValueAtom);
  const setIsEmployerCompanyActionModalOpen = useSetAtom(isEmployerCompanyActionModalOpenAtom);
  const [newlyCreatedCompanyId, setNewlyCreatedCompanyId] = useAtom(newlyCreatedCompanyIdAtom);

  // After a company is created from the empty state, redirect to manage payment methods
  useEffect(() => {
    if (newlyCreatedCompanyId) {
      setNewlyCreatedCompanyId('');
      router.push('/employer/manage-payment-methods');
    }
  }, [newlyCreatedCompanyId, setNewlyCreatedCompanyId, router]);

  const handleAddCompany = useCallback(() => {
    setCurrentCompanyAction('searchCompany');
    setLastCompanySearchValue('');
    setIsEmployerCompanyActionModalOpen(true);
  }, [setCurrentCompanyAction, setLastCompanySearchValue, setIsEmployerCompanyActionModalOpen]);

  const handleAddPaymentMethod = useCallback(() => {
    router.push('/employer/manage-payment-methods');
  }, [router]);

  const isEmpty = !isLoading && (!companyList || companyList.length === 0);

  return (
    <>
      <CardsWrapper>
        {isLoading ? (
          loadingItemsEl
        ) : isEmpty ? (
          <EmptyCompanyPaymentCard onAddCompany={handleAddCompany} onAddPaymentMethod={handleAddPaymentMethod} />
        ) : (
          companyListItemsEl
        )}
      </CardsWrapper>
    </>
  );
}
export default EmployerCompanyList;

export function CompanyItem({ company, stripePaymentMethods }: CompanyItemProps) {
  const { t } = useTranslation('payment-and-invoices');
  const router = useRouter();
  const setSelectedCompany = useSetAtom(selectedCompanyAtom);
  const setIsEmployerCompanyActionModalOpen = useSetAtom(isEmployerCompanyActionModalOpenAtom);
  const setCurrentCompanyAction = useSetAtom(currentCompanyActionAtom);

  const paymentMethods = useMemo(() => {
    const companyPaymentData = stripePaymentMethods?.find((method) => method.company_id === company.id);
    return companyPaymentData?.payment_methods || [];
  }, [company.id, stripePaymentMethods]);

  const handleEdit = useCallback(
    (company: Company) => {
      setSelectedCompany(company);
      setCurrentCompanyAction('editCompany');
      setIsEmployerCompanyActionModalOpen(true);
    },
    [setCurrentCompanyAction, setIsEmployerCompanyActionModalOpen, setSelectedCompany]
  );

  const handleManagePaymentMethod = useCallback(
    (_company: Company) => {
      router.push('/employer/manage-payment-methods');
    },
    [router]
  );

  return (
    <CompanyPaymentInfoCard
      company={company}
      paymentMethods={paymentMethods}
      orientation="horizontal"
      onEditCompany={handleEdit}
      onChangePaymentMethod={handleManagePaymentMethod}
      editLabel={t('edit')}
      changeLabel={t('change')}
      billingInfoLabel={t('billing_information')}
      paymentMethodLabel={t('payment_method')}
      vatLabel={t('vat_label')}
      defaultBadgeLabel={t('default')}
      addLabel={t('add')}
    />
  );
}

function useEmployerCompanyList({ context, limit }: { context: string; limit?: number }) {
  const { companyList, query: companyListQuery } = useEmployerCompanyListQuery({
    params: { context, limit },
  });
  const { stripePaymentMethods, query: paymentMethodsQuery } = useEmployerStripePaymentMethodsQuery();

  const companyListItemsEl = useMemo(
    () =>
      companyList?.map((company) => {
        return <CompanyItem key={company.id} company={company} stripePaymentMethods={stripePaymentMethods} />;
      }),
    [companyList, stripePaymentMethods]
  );
  const loadingItemsEl = useMemo(
    () => Array.from({ length: limit }).map((_, index) => <SkeletonEmployerCardLoading key={index} />),
    [limit]
  );

  const isLoading = companyListQuery.isLoading || paymentMethodsQuery.isLoading;

  return {
    companyList,
    isLoading,
    companyListItemsEl,
    loadingItemsEl,
  };
}

interface CompanyItemProps {
  company: Company;
  stripePaymentMethods: IndexStripePaymentMethods;
}
